// PartnerDetail › Kiosks (DESIGN § 8.1; a page of its own until 2026-10-22): a partner's kiosks, e.g. GSC's machines. A
// kiosk belongs to a client and stands at one of its locations (or the client itself): a client lists all of its kiosks,
// a location the ones standing there. Each has its own client id and secret (the secret shown once, here, when it is
// made or rotated), the kiosk operator's terminal id — every kiosk call must send it, and every load keeps it — a
// per-load limit, and can be switched off.
import { shortDate } from '@qor/web-shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { adminApi, kioskApi, type KioskCredential, type KioskDevice, type ProgramSummary } from '../api';
import { useStaff } from '../components/RequireStaff';
import { confirmed } from '../components/ui';
import { KioskCardStock } from './KioskCardStock';

type Shown = { title: string; credential: KioskCredential };

const blankToNull = (v: FormDataEntryValue | null) => {
  const s = String(v ?? '').trim();
  return s === '' ? null : s;
};

export function PartnerKiosksPanel({
  partner,
  after,
}: {
  partner: ProgramSummary;
  after: (ok: boolean, message: string) => void;
}) {
  const client = useQueryClient();
  const { user, scope } = useStaff();
  const editable = user.role === 'SystemAdmin' || user.role === 'ProgramAdmin';
  const devices = useQuery({ queryKey: ['kiosks'], queryFn: kioskApi.devices });
  const programs = useQuery({ queryKey: ['programs'], queryFn: adminApi.programs });
  const [shown, setShown] = useState<Shown | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // a kiosk's program is always the client; where it stands is the client itself or one of its locations
  const isLocation = partner.parentProgramId !== null;
  const programId = partner.parentProgramId ?? partner.id;
  const all = programs.data ?? [];
  const nameOf = (id: string) => all.find((p) => p.id === id)?.displayName ?? '—';
  const locations = all.filter((p) => p.parentProgramId === partner.id);

  async function done(ok: boolean, message: string) {
    setBusy(false);
    after(ok, message);
    await client.invalidateQueries({ queryKey: ['kiosks'] });
  }

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    setBusy(true);
    const r = await kioskApi.create({
      code: String(f.get('Code') ?? '').trim(),
      name: String(f.get('Name') ?? '').trim(),
      programId,
      locationId: isLocation ? partner.id : blankToNull(f.get('LocationId')),
      terminalId: blankToNull(f.get('TerminalId')),
      maxLoad: blankToNull(f.get('MaxLoad')),
    });
    if (r.ok) {
      setShown({ title: `Credentials for ${r.data.device.code}`, credential: r.data });
      form.reset();
    }
    await done(r.ok, r.ok ? `Kiosk ${r.data.device.code} added.` : r.error);
  }

  async function save(d: KioskDevice, e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    const r = await kioskApi.change(d.id, {
      name: String(f.get('Name') ?? '').trim(),
      terminalId: blankToNull(f.get('TerminalId')),
      maxLoad: blankToNull(f.get('MaxLoad')),
    });
    if (r.ok) setEditing(null);
    await done(r.ok, r.ok ? `Kiosk ${d.code} saved.` : r.error);
  }

  async function rotate(d: KioskDevice) {
    if (!confirmed(`Give ${d.code} a new secret? The old one keeps working for 24 hours.`)) return;
    setBusy(true);
    const r = await kioskApi.rotate(d.id);
    if (r.ok) setShown({ title: `New secret for ${d.code}`, credential: r.data });
    await done(r.ok, r.ok ? 'New secret made.' : r.error);
  }

  async function toggle(d: KioskDevice) {
    const off = d.status === 'active';
    if (off && !confirmed(`Switch ${d.code} off? It stops working at its next call.`)) return;
    setBusy(true);
    const r = await kioskApi.change(d.id, { status: off ? 'disabled' : 'active' });
    await done(r.ok, r.ok ? (off ? `${d.code} switched off.` : `${d.code} switched on.`) : r.error);
  }

  const list = (devices.data ?? []).filter((d) =>
    isLocation ? d.locationId === partner.id : d.programId === partner.id,
  );
  return (
    <>
      <div className="qc-panel">
        <div className="qc-panel-title">
          <h3>Kiosks</h3>
          <span>
            {isLocation
              ? `The kiosks standing at ${partner.displayName}. `
              : `The kiosks of ${partner.displayName}, at the partner or one of its locations. `}
            Each calls the kiosk API with its own credentials and must send its terminal id; a kiosk without one is
            refused.
          </span>
        </div>

        {shown && (
          <div className="qc-auth-error" role="alert" style={{ marginBottom: 12 }}>
            <strong>{shown.title}</strong> — copy now: this is the only time the secret is shown.
            <dl className="qc-detail-dl">
              <div>
                <dt>Client ID</dt>
                <dd>
                  <code>{shown.credential.clientId}</code>
                </dd>
              </div>
              <div>
                <dt>Client secret</dt>
                <dd>
                  <code>{shown.credential.clientSecret}</code>
                </dd>
              </div>
            </dl>
            <button type="button" className="qc-button-ghost" onClick={() => setShown(null)}>
              I have copied it
            </button>
          </div>
        )}

        {list.length === 0 ? (
          <p className="qc-empty">{devices.isPending ? 'Loading…' : 'No kiosk here yet.'}</p>
        ) : (
          <table className="qc-plain-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Terminal ID</th>
                <th>Location</th>
                <th>Max load</th>
                <th>Status</th>
                <th>Last seen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((d) =>
                editing === d.id ? (
                  <tr key={d.id}>
                    <td colSpan={8}>
                      <form onSubmit={(e) => void save(d, e)} className="qc-cardholder-filters">
                        <strong>{d.code}</strong>
                        <input name="Name" defaultValue={d.name} aria-label="Name" required maxLength={160} />
                        <input
                          name="TerminalId"
                          defaultValue={d.terminalId ?? ''}
                          placeholder="Terminal ID"
                          aria-label="Terminal ID"
                          maxLength={64}
                        />
                        <input
                          name="MaxLoad"
                          defaultValue={d.maxLoad ?? ''}
                          placeholder="Max load (USD)"
                          aria-label="Max load"
                          inputMode="decimal"
                        />
                        <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
                          Save
                        </button>
                        <button type="button" className="qc-button-ghost" onClick={() => setEditing(null)}>
                          Cancel
                        </button>
                      </form>
                    </td>
                  </tr>
                ) : (
                  <tr key={d.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <code>{d.code}</code>
                    </td>
                    <td>{d.name}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {d.terminalId ? (
                        <code>{d.terminalId}</code>
                      ) : (
                        <span className="qc-badge qc-badge-danger">Not set</span>
                      )}
                    </td>
                    <td>{d.locationId === d.programId ? 'The partner itself' : nameOf(d.locationId)}</td>
                    <td>{d.maxLoad ?? 'Program limit'}</td>
                    <td>
                      <span className={`qc-badge ${d.status === 'active' ? 'qc-badge-success' : 'qc-badge-muted'}`}>
                        {d.status === 'active' ? 'Active' : 'Off'}
                      </span>
                    </td>
                    <td>{d.lastSeenAt ? shortDate(d.lastSeenAt) : 'Never'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {editable && (
                        <>
                          <button type="button" className="qc-button-ghost" onClick={() => setEditing(d.id)}>
                            Edit
                          </button>{' '}
                          <button
                            type="button"
                            className="qc-button-ghost"
                            disabled={busy}
                            onClick={() => void rotate(d)}
                          >
                            New secret
                          </button>{' '}
                          <button
                            type="button"
                            className="qc-button-ghost"
                            disabled={busy}
                            onClick={() => void toggle(d)}
                          >
                            {d.status === 'active' ? 'Switch off' : 'Switch on'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
      </div>

      {scope.internal && (
        <KioskCardStock
          partner={partner}
          locations={locations}
          devices={list}
          after={(ok, message) => void done(ok, message)}
        />
      )}

      {editable && (
        <div className="qc-panel" style={{ marginTop: 20 }}>
          <div className="qc-panel-title">
            <h3>Add kiosk</h3>
            <span>The client secret is shown once, when the kiosk is added.</span>
          </div>
          <form onSubmit={(e) => void create(e)} className="qc-form-grid">
            <label className="qc-field">
              <span>Code</span>
              <input name="Code" placeholder="KIOSK-GSC-0042" required maxLength={64} />
            </label>
            <label className="qc-field">
              <span>Name</span>
              <input name="Name" placeholder="Front desk" required maxLength={160} />
            </label>
            <label className="qc-field">
              <span>Location</span>
              {isLocation ? (
                <p className="qc-static-value">{partner.displayName}</p>
              ) : (
                <select name="LocationId" defaultValue="">
                  <option value="">{partner.displayName} itself</option>
                  {locations.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}
                    </option>
                  ))}
                </select>
              )}
            </label>
            <label className="qc-field">
              <span>Terminal ID</span>
              <input name="TerminalId" placeholder="The kiosk operator's id, e.g. GSC-T-0042" maxLength={64} />
              <small className="qc-field-hint">Required before the kiosk can be used.</small>
            </label>
            <label className="qc-field">
              <span>Max load (USD)</span>
              <input name="MaxLoad" placeholder="Empty: the program's limit" inputMode="decimal" />
            </label>
            <div className="qc-form-actions">
              <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
                Add kiosk
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
